import type { UpdateWorkOrderInput, WorkOrder } from '@/domain/work-order.types'
import { useUpdateWorkOrder } from '@/hooks/useWorkOrders'
import { apiErrorMessage } from '@/lib/api-error'
import { notification } from '@/notifications/notification-store'

/**
 * Puts one user in one of the order's people slots and reports the outcome.
 *
 * The write is optimistic, so a rejected one reverts silently: the status bar is what
 * tells the user the assignment did not take. `name` is the one the picker showed, so
 * the message says whoever the user actually chose rather than a user id.
 */
function useAssign(
	workOrder: Pick<WorkOrder, 'workOrderId' | 'orderCode'>,
	patchFor: (userId: string | undefined) => UpdateWorkOrderInput,
	slot: string,
) {
	const update = useUpdateWorkOrder(workOrder.workOrderId)
	const { orderCode } = workOrder
	return (userId: string | undefined, name: string | undefined) =>
		update.mutate(
			{ id: workOrder.workOrderId, patch: patchFor(userId) },
			{
				onSuccess: () =>
					notification.success(name ? `${orderCode} assigned to ${name}${slot}.` : `${orderCode} is now unassigned.`),
				onError: (error) =>
					notification.error(
						apiErrorMessage(
							error,
							name ? `Could not assign ${orderCode} to ${name}${slot}.` : `Could not unassign ${orderCode}.`,
						),
					),
			},
		)
}

/** Makes one user the order's only assignee, its designer. */
export const useAssignDesigner = (workOrder: Pick<WorkOrder, 'workOrderId' | 'orderCode'>) =>
	useAssign(workOrder, (userId) => ({ assigneeIds: userId ? [userId] : [] }), '')

/** Makes one user the order's PM. */
export const useAssignPm = (workOrder: Pick<WorkOrder, 'workOrderId' | 'orderCode'>) =>
	useAssign(workOrder, (userId) => ({ pmUserId: userId ?? null }), ' as PM')
